/**
 * Iter121G — Generic Case Entity View Page
 *
 * Route: /cases/:caseId/:entity/:id/view
 *   where entity ∈ {subjects, vehicles, leads, interviews, notes, locations}
 *
 * Renders every field of the entity as a labelled read-only card grid, with
 * an "Edit" button that jumps to the matching form page. Fixes the reported
 * bug where clicking "View" opened an empty Edit form.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import {
  ArrowLeft, Pencil, Trash2, RefreshCw, User, Car, Lightbulb,
  MessageSquare, MapPin, StickyNote, ExternalLink, Navigation2,
} from 'lucide-react';

const TOKEN_KEY = 'nexus_token';

// entity → { fetchUrl, deleteUrl, editUrl, listTab, title, icon, sections }
const ENTITY_CONFIG = {
  subjects: {
    label: 'Person',
    tab: 'subjects',
    icon: User,
    fetchUrl: (id) => `${BACKEND_URL}/api/subjects/${id}`,
    deleteUrl: (id) => `${BACKEND_URL}/api/subjects/${id}`,
    editUrl: (caseId, id) => `/cases/${caseId}/subjects/${id}/edit`,
    titleField: 'full_name',
    subtitleField: 'role_in_case',
    sections: [
      {
        heading: 'Identity',
        fields: [
          ['Full Name', 'full_name'],
          ['Role in Case', 'role_in_case'],
          ['Aliases', 'aliases'],
          ['Date of Birth', 'date_of_birth'],
          ['Gender', 'gender'],
          ['Race / Ethnicity', 'race_ethnicity'],
          ['Surveillance Risk Level', 'surveillance_risk_level'],
        ],
      },
      {
        heading: 'Physical Description',
        fields: [
          ['Height', 'height'],
          ['Weight', 'weight'],
          ['Hair Color', 'hair_color'],
          ['Eye Color', 'eye_color'],
          ['Physical Description', 'physical_description', { long: true }],
          ['Scars / Tattoos / Marks', 'scars_tattoos_marks', { long: true }],
        ],
      },
      {
        heading: 'Contact',
        fields: [
          ['Employer', 'employer'],
          ['Last Known Address', 'last_known_address'],
          ['Phone Numbers', 'phone_numbers'],
          ['Email Addresses', 'email_addresses'],
          ['Social Media', 'social_media'],
        ],
      },
      {
        heading: 'Background',
        fields: [
          ['Criminal History', 'criminal_history', { long: true }],
          ['Caution Indicators', 'caution_indicators', { long: true }],
          ['Notes', 'notes', { long: true }],
        ],
      },
    ],
  },
  vehicles: {
    label: 'Vehicle',
    tab: 'vehicles',
    icon: Car,
    fetchUrl: (id) => `${BACKEND_URL}/api/vehicles/${id}`,
    deleteUrl: (id) => `${BACKEND_URL}/api/vehicles/${id}`,
    editUrl: (caseId, id) => `/cases/${caseId}/vehicles/${id}/edit`,
    titleField: 'license_plate',
    subtitleField: null,
    sections: [
      {
        heading: 'Vehicle',
        fields: [
          ['License Plate', 'license_plate'],
          ['State', 'state'],
          ['Year', 'year'],
          ['Make', 'make'],
          ['Model', 'model'],
          ['Color', 'color'],
        ],
      },
      {
        heading: 'Ownership',
        fields: [
          ['Registered Owner', 'registered_owner'],
          ['Notes', 'notes', { long: true }],
        ],
      },
    ],
  },
  leads: {
    label: 'Lead',
    tab: 'leads',
    icon: Lightbulb,
    fetchUrl: (id) => `${BACKEND_URL}/api/leads/${id}`,
    deleteUrl: (id) => `${BACKEND_URL}/api/leads/${id}`,
    editUrl: (caseId, id) => `/cases/${caseId}/leads/${id}/edit`,
    titleField: 'lead_source',
    subtitleField: 'status',
    sections: [
      {
        heading: 'Lead',
        fields: [
          ['Lead Source', 'lead_source'],
          ['Status', 'status'],
          ['Priority', 'priority'],
          ['Received Date', 'received_date'],
          ['Assigned Investigator', 'assigned_investigator_name'],
        ],
      },
      {
        heading: 'Details',
        fields: [
          ['Description', 'description', { long: true }],
          ['Notes', 'notes', { long: true }],
        ],
      },
    ],
  },
  interviews: {
    label: 'Interview',
    tab: 'interviews',
    icon: MessageSquare,
    fetchUrl: (id) => `${BACKEND_URL}/api/interviews/${id}`,
    deleteUrl: (id) => `${BACKEND_URL}/api/interviews/${id}`,
    editUrl: (caseId, id) => `/cases/${caseId}/interviews/${id}/edit`,
    titleField: 'interviewee_name',
    subtitleField: 'relationship_to_case',
    sections: [
      {
        heading: 'Interview',
        fields: [
          ['Interviewee', 'interviewee_name'],
          ['Relationship to Case', 'relationship_to_case'],
          ['Interview Date', 'interview_date'],
          ['Interview Location', 'interview_location'],
          ['Interviewer', 'interviewer_name'],
        ],
      },
      {
        heading: 'Findings',
        fields: [
          ['Observations', 'observations', { long: true }],
          ['Body Language Notes', 'body_language_notes', { long: true }],
          ['Evidence Collected', 'evidence_collected', { long: true }],
          ['Summary', 'summary', { long: true }],
        ],
      },
    ],
  },
  notes: {
    label: 'Case Note',
    tab: 'notes',
    icon: StickyNote,
    fetchUrl: (id) => `${BACKEND_URL}/api/case-notes/${id}`,
    deleteUrl: (id) => `${BACKEND_URL}/api/case-notes/${id}`,
    editUrl: (caseId, id) => `/cases/${caseId}/notes/${id}/edit`,
    titleField: 'subject',
    subtitleField: 'category',
    sections: [
      {
        heading: 'Note',
        fields: [
          ['Subject', 'subject'],
          ['Category', 'category'],
          ['Visibility', 'visibility'],
          ['Follow-up Required', 'follow_up_required'],
          ['Created By', 'created_by_name'],
        ],
      },
      {
        heading: 'Content',
        fields: [
          ['Content', 'content', { long: true }],
        ],
      },
    ],
  },
  locations: {
    label: 'Location',
    tab: 'locations',
    icon: MapPin,
    fetchUrl: (id) => `${BACKEND_URL}/api/locations/${id}`,
    deleteUrl: (id) => `${BACKEND_URL}/api/locations/${id}`,
    editUrl: (caseId, id) => `/cases/${caseId}/locations/${id}/edit`,
    titleField: 'name',
    subtitleField: 'location_type',
    sections: [
      {
        heading: 'Location',
        fields: [
          ['Name', 'name'],
          ['Type', 'location_type'],
          ['Address', 'address'],
          ['City', 'city'],
          ['State', 'state'],
          ['ZIP', 'zip_code'],
        ],
      },
      {
        heading: 'GPS',
        fields: [
          ['Latitude', 'latitude'],
          ['Longitude', 'longitude'],
        ],
      },
      {
        heading: 'Details',
        fields: [
          ['Description', 'description', { long: true }],
          ['Notes', 'notes', { long: true }],
        ],
      },
    ],
  },
};

const asDisplay = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (Array.isArray(v)) return v.filter(Boolean).join(', ') || null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
};

export default function EntityViewPage() {
  const { caseId, entity, id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const cfg = ENTITY_CONFIG[entity];

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const goBack = useCallback(() => {
    navigate(`/cases/${caseId}?tab=${cfg?.tab || ''}`);
  }, [caseId, cfg, navigate]);

  const load = useCallback(async () => {
    if (!cfg) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(cfg.fetchUrl(id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      setItem(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [cfg, id, token]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete this ${cfg.label.toLowerCase()}?\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetch(cfg.deleteUrl(id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Delete failed (${res.status})`);
      }
      toast({ title: 'Deleted', description: `${cfg.label} removed` });
      goBack();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
  };

  const openDirections = () => {
    if (!item) return;
    const fullAddress = [item.address, item.city, item.state, item.zip_code].filter(Boolean).join(', ');
    const params = new URLSearchParams();
    if (item.latitude && item.longitude) {
      params.set('destination_lat', String(item.latitude));
      params.set('destination_lng', String(item.longitude));
    }
    if (fullAddress) params.set('destination_address', fullAddress);
    if (item.name) params.set('destination_label', item.name);
    params.set('directions', '1');
    navigate(`/case-address-map?${params.toString()}`);
  };

  if (!cfg) {
    return (
      <div className="max-w-lg mx-auto p-6 text-center">
        <p className="text-slate-300">Unknown entity type: <span className="font-mono">{entity}</span></p>
        <Button onClick={() => navigate(`/cases/${caseId}`)} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Case
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-300 p-6">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading {cfg.label.toLowerCase()}…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto p-6 text-center">
        <p className="text-red-400 mb-3">{error}</p>
        <Button onClick={goBack} className="bg-blue-600 hover:bg-blue-700 text-white">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Case
        </Button>
      </div>
    );
  }

  const Icon = cfg.icon || User;
  const title = asDisplay(item?.[cfg.titleField]) || `Untitled ${cfg.label}`;
  const subtitle = cfg.subtitleField ? asDisplay(item?.[cfg.subtitleField]) : null;

  return (
    <div className="max-w-4xl mx-auto space-y-5 pb-24" data-testid="entity-view-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          onClick={goBack}
          variant="outline"
          className="border-slate-600 !text-slate-200 hover:bg-slate-800"
          data-testid="entity-view-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Case
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wider">
            <Icon className="w-3.5 h-3.5" /> {cfg.label}
          </div>
          <h1 className="text-2xl font-bold text-white break-words" data-testid="entity-view-title">
            {title}
          </h1>
          {subtitle && (
            <Badge className="mt-1 bg-slate-700 text-slate-200 border-slate-600">
              {subtitle}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {entity === 'locations' && (item?.latitude || item?.address) && (
            <Button
              onClick={openDirections}
              variant="outline"
              className="border-blue-500/40 !text-blue-300 hover:bg-blue-950/40"
              data-testid="entity-view-directions-btn"
            >
              <Navigation2 className="w-4 h-4 mr-1" /> Directions
            </Button>
          )}
          <Button
            onClick={() => navigate(cfg.editUrl(caseId, id))}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="entity-view-edit-btn"
          >
            <Pencil className="w-4 h-4 mr-1" /> Edit
          </Button>
          <Button
            onClick={handleDelete}
            variant="outline"
            className="border-red-700/60 !text-red-300 hover:bg-red-900/30"
            data-testid="entity-view-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
      </div>

      {cfg.sections.map((section) => {
        const rows = section.fields
          .map(([label, key, opts = {}]) => ({ label, key, opts, value: asDisplay(item?.[key]) }))
          .filter((r) => r.value !== null);
        if (rows.length === 0) return null;
        return (
          <div
            key={section.heading}
            className="bg-slate-800 rounded-lg border border-slate-700 p-4 sm:p-5"
            data-testid={`entity-view-section-${section.heading.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
              {section.heading}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {rows.map((r) => (
                <div
                  key={r.key}
                  className={r.opts?.long ? 'sm:col-span-2' : ''}
                  data-testid={`entity-view-field-${r.key}`}
                >
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{r.label}</p>
                  {r.opts?.long ? (
                    <p className="text-sm text-white whitespace-pre-wrap break-words leading-relaxed">
                      {r.value}
                    </p>
                  ) : (
                    <p className="text-sm text-white break-words">{r.value}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {item?.photo_url && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-4 sm:p-5">
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Photo</h2>
          <a href={item.photo_url} target="_blank" rel="noopener noreferrer" className="inline-block group">
            <img
              src={item.photo_url}
              alt={title}
              className="max-h-72 rounded border border-slate-700"
              data-testid="entity-view-photo"
            />
            <p className="mt-1 text-xs text-blue-400 group-hover:underline flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Open full size
            </p>
          </a>
        </div>
      )}
    </div>
  );
}
